// Pure layout and coordinate math for the PDF viewer. Everything here works in the
// viewer's base coordinate system: CSS px at 96 dpi, i.e. what a page measures at
// 100 % zoom. Zoom is applied on top with a CSS transform, so nothing in this file
// depends on the viewport or on the current zoom unless it says so explicitly.

export const PT_TO_PX = 96 / 72;

export const ptToPx = (pt: number) => pt * PT_TO_PX;
export const pxToPt = (px: number) => px / PT_TO_PX;

export type Size = { width: number; height: number };

export type PageBox = {
	index: number;
	left: number;
	top: number;
	width: number;
	height: number;
};

export type DocumentLayout = {
	pages: PageBox[];
	width: number;
	height: number;
};

export const LAYOUT = {
	/** Space around the stack of pages. */
	PADDING: 20,
	/** Room under each page for its "n de N" label. */
	LABEL_HEIGHT: 28,
	/** Space between one page's label and the next page. */
	GAP: 12,
} as const;

/** Stacks pages vertically, centred on the widest one. Sizes are in base px. */
export function computeDocumentLayout(
	pageSizes: Size[],
	options: Partial<typeof LAYOUT> = {},
): DocumentLayout {
	const { PADDING, LABEL_HEIGHT, GAP } = { ...LAYOUT, ...options };
	const maxWidth = pageSizes.reduce(
		(max, size) => Math.max(max, size.width),
		0,
	);
	let top = PADDING;
	const pages = pageSizes.map((size, index) => {
		const box: PageBox = {
			index,
			left: PADDING + (maxWidth - size.width) / 2,
			top,
			width: size.width,
			height: size.height,
		};
		top += size.height + LABEL_HEIGHT + GAP;
		return box;
	});
	return {
		pages,
		width: maxWidth + PADDING * 2,
		// The last page keeps its label but needs no gap after it.
		height: pages.length ? top - GAP + PADDING : PADDING * 2,
	};
}

/**
 * Index of the page whose vertical band (page plus its label and the gap below it)
 * contains `y`. Binary search, so it stays cheap for long documents.
 */
export function findPageIndexAtY(layout: DocumentLayout, y: number): number {
	const { pages } = layout;
	if (pages.length === 0) return -1;
	let low = 0;
	let high = pages.length - 1;
	while (low < high) {
		const mid = Math.ceil((low + high) / 2);
		if (pages[mid].top <= y) low = mid;
		else high = mid - 1;
	}
	return low;
}

/**
 * Converts a point in document base px into page-relative base px. Returns null when
 * the point is outside every page (on the background, padding or a label).
 */
export function documentPointToPage(
	layout: DocumentLayout,
	x: number,
	y: number,
): { pageIndex: number; x: number; y: number } | null {
	const index = findPageIndexAtY(layout, y);
	if (index < 0) return null;
	const page = layout.pages[index];
	const localX = x - page.left;
	const localY = y - page.top;
	if (
		localX < 0 ||
		localY < 0 ||
		localX > page.width ||
		localY > page.height
	) {
		return null;
	}
	return { pageIndex: index, x: localX, y: localY };
}

/**
 * The page that best represents the viewport: the one covering most of the visible
 * band [scrollTop, scrollTop + viewportHeight], both in base px.
 */
export function findCurrentPageIndex(
	layout: DocumentLayout,
	viewportTop: number,
	viewportHeight: number,
): number {
	const { pages } = layout;
	if (pages.length === 0) return -1;
	const viewportBottom = viewportTop + viewportHeight;
	let best = findPageIndexAtY(layout, viewportTop);
	let bestVisible = -1;
	for (let i = Math.max(0, best); i < pages.length; i++) {
		const page = pages[i];
		if (page.top > viewportBottom) break;
		const visible =
			Math.min(page.top + page.height, viewportBottom) -
			Math.max(page.top, viewportTop);
		if (visible > bestVisible) {
			bestVisible = visible;
			best = i;
		}
	}
	return best;
}

// --- Zoom ---------------------------------------------------------------------

/** Mirrors DocuSign: 96 → 150 → 250 dpi upwards, plus 75 % and 50 % downwards. */
export const ZOOM_STEPS = [0.5, 0.75, 1, 150 / 96, 250 / 96] as const;
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 4;

export const clampZoom = (zoom: number) =>
	Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

const ZOOM_EPSILON = 0.001;

export function nextZoomStep(zoom: number): number {
	const step = ZOOM_STEPS.find(
		(candidate) => candidate > zoom + ZOOM_EPSILON,
	);
	return step ?? zoom;
}

export function previousZoomStep(zoom: number): number {
	const smaller = ZOOM_STEPS.filter(
		(candidate) => candidate < zoom - ZOOM_EPSILON,
	);
	return smaller.length ? smaller[smaller.length - 1] : zoom;
}

/** Zoom that makes the widest page (plus padding) exactly fill the viewport. */
export function fitWidthZoom(viewportWidth: number, contentWidth: number) {
	if (viewportWidth <= 0 || contentWidth <= 0) return 1;
	return clampZoom(viewportWidth / contentWidth);
}

export const formatZoom = (zoom: number) => `${Math.round(zoom * 100)} %`;

/**
 * Horizontal offset at which the zoom container sits inside the scroll viewport: it
 * is centred while narrower than the viewport, left-aligned (and scrollable) after.
 */
export const centeringOffset = (
	viewportSize: number,
	scaledContentSize: number,
) => Math.max(0, (viewportSize - scaledContentSize) / 2);

/** One scroll axis of the viewer at a given zoom. */
type ScrollAxis = {
	/** Unscaled content size along this axis (base px). */
	contentSize: number;
	viewportSize: number;
	/** Whether the content is centred on this axis while smaller than the viewport. */
	center?: boolean;
};

const axisOffset = (axis: ScrollAxis, zoom: number) =>
	axis.center
		? centeringOffset(axis.viewportSize, axis.contentSize * zoom)
		: 0;

/**
 * Document coordinate (base px, along one axis) under `anchor`, a point measured
 * from the viewport's leading edge, at the given scroll and zoom.
 */
export function viewportToDocument(
	params: ScrollAxis & { anchor: number; scroll: number; zoom: number },
): number {
	return (
		(params.anchor + params.scroll - axisOffset(params, params.zoom)) /
		params.zoom
	);
}

/**
 * Scroll position that puts document coordinate `point` under `anchor` at `zoom`,
 * clamped to the scrollable range.
 */
export function documentToScroll(
	params: ScrollAxis & { point: number; anchor: number; zoom: number },
): number {
	const next =
		params.point * params.zoom +
		axisOffset(params, params.zoom) -
		params.anchor;
	const maxScroll = Math.max(
		0,
		params.contentSize * params.zoom - params.viewportSize,
	);
	return Math.min(maxScroll, Math.max(0, next));
}

/**
 * New scroll position along one axis that keeps the document point under `anchor`
 * (measured from the viewport's leading edge) fixed while zoom goes from `oldZoom`
 * to `newZoom`. `center` says whether the content is centred on this axis when it
 * is smaller than the viewport.
 */
export function anchoredScroll(
	params: ScrollAxis & {
		anchor: number;
		scroll: number;
		oldZoom: number;
		newZoom: number;
	},
): number {
	const point = viewportToDocument({ ...params, zoom: params.oldZoom });
	return documentToScroll({ ...params, point, zoom: params.newZoom });
}

// --- Rendering ------------------------------------------------------------------

/**
 * Largest canvas, in backing-store pixels. iOS Safari refuses canvases above
 * 16,777,216 px (and pdf.js slows down long before), so stay a little under it.
 */
export const MAX_CANVAS_PIXELS = 16_000_000;
/** Device px per base px never exceeds this, whatever the zoom and screen. */
export const MAX_PIXEL_RATIO = 4;
/**
 * Lower ceiling on phones and tablets (dpr >= 2.5 or a coarse primary pointer):
 * their bitmaps are already large at 100 %, and they have the least memory (iOS
 * also caps a page's total canvas memory).
 */
export const MAX_PIXEL_RATIO_CONSTRAINED = 3;
/** Quick first pass shown while the sharp bitmap renders (~50 dpi, like thumbnails). */
export const PREVIEW_PIXEL_RATIO = 0.5;

/** Screens that get the lower raster ceiling and render-cache budget. */
export const isConstrainedDevice = (
	devicePixelRatio: number,
	isCoarsePointer = false,
) => devicePixelRatio >= 2.5 || isCoarsePointer;

export const maxPixelRatio = (
	devicePixelRatio: number,
	isCoarsePointer = false,
) =>
	isConstrainedDevice(devicePixelRatio, isCoarsePointer)
		? MAX_PIXEL_RATIO_CONSTRAINED
		: MAX_PIXEL_RATIO;

/** Backing-store size of a page canvas, rounded up the way the canvas is sized. */
export const canvasSize = (size: Size, ratio: number): Size => ({
	width: Math.ceil(size.width * ratio),
	height: Math.ceil(size.height * ratio),
});

/**
 * Backing-store scale (device px per base px) for a page rendered at `zoom` on a
 * screen with `devicePixelRatio`: min(zoom · dpr, 4), or 3 on phones and tablets
 * (see isConstrainedDevice). If
 * the rounded-up canvas would still exceed `maxPixels` (unusually large pages), the
 * scale is reduced until it fits, so the page renders less sharp rather than not
 * at all.
 */
export function canvasPixelRatio(
	size: Size,
	zoom: number,
	devicePixelRatio: number,
	options: { isCoarsePointer?: boolean; maxPixels?: number } = {},
): number {
	const maxPixels = options.maxPixels ?? MAX_CANVAS_PIXELS;
	let ratio = Math.min(
		zoom * devicePixelRatio,
		maxPixelRatio(devicePixelRatio, options.isCoarsePointer),
	);
	const fits = (r: number) => {
		const canvas = canvasSize(size, r);
		return canvas.width * canvas.height <= maxPixels;
	};
	if (fits(ratio)) return ratio;
	ratio = Math.sqrt(maxPixels / (size.width * size.height));
	// Rounding each side up can add up to (width + height) px; step down until the
	// actual canvas fits.
	while (!fits(ratio)) ratio *= 0.999;
	return ratio;
}
